import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import { cloneDeep } from 'lodash';
import { H4, H5, Input, Checkbox } from 'design-system-web';
import {
  PropertiesInterface,
  Channel,
  type PropertyType,
} from 'application-types';
import { DomainTagInput } from '../DomainTagInput/DomainTagInput';
import {
  ChannelIntegrationEditorProps,
  Integration,
  ValidationErrorType,
} from '../../types/ChannelIntegrationEditorTypes';
import styles from './VantifyEditor.scss';
import { LoadSettingButton } from '../LoadSettingButton/LoadSettingButton';
import { VantifyTenantMapping } from '../VantifyTenantMapping/VantifyTenantMapping';
import {
  useVantifyUser,
  useVantifySiteDetails,
} from 'integrations-vantify-react-hooks';
import type {
  InputType,
  VantifyTenantMappingData,
  VantifySettings,
  VantifyFieldProps,
  LoadSettingsButtonCallbackParams,
  UseVantifyEditorStateParams,
  VantifyEditorProps,
} from '../../types/VantifyEditorTypes';

const collectFieldErrors = (
  error: ValidationErrorType | null | undefined,
  targetPath: string
): string[] => {
  if (!error) {
    return [];
  }

  const currentLevelErrors =
    error.path === targetPath && Array.isArray(error.errors)
      ? error.errors.filter((err): err is string => typeof err === 'string')
      : [];

  const nestedErrors = error.inner
    ? error.inner.flatMap(innerError =>
        collectFieldErrors(innerError, targetPath)
      )
    : [];

  const allErrors = [...currentLevelErrors, ...nestedErrors];
  
  if (allErrors.length > 1) {
    const urlValidationError = allErrors.find(
      err =>
      err.toLowerCase().includes('valid url') || 
      err.toLowerCase().includes('must be a url')
    );
    
    if (urlValidationError) {
      return [urlValidationError];
    }
  }

  return allErrors;
};

const FieldErrorMessages: React.FC<{ errors?: string[] | null }> = ({
  errors,
}) => {
  if (!errors?.length) {
    return null;
  }

  return (
    <div className={styles.bottomBox}>
      {errors.map(error => (
        <p key={error} className={styles.error}>
          {error}
        </p>
      ))}
    </div>
  );
};

const LoadSettingButtonField: React.FC<
  VantifyFieldProps & { forCreate?: boolean }
> = ({
  metadataLoading,
  metadataError,
  loadSettingsButtonCallback,
  integrationIsDeleted,
  validateMetadataRequestFields,
  channelIntegration,
  definition,
  forCreate,
}) => {
  const buttonText = !forCreate
    ? 'web.admin.channel.integrations.vantify.settings.re-load settings'
    : 'web.admin.channel.integrations.vantify.settings.load-settings';

  return (
    <LoadSettingButton
      loading={metadataLoading}
      error={metadataError}
      callback={loadSettingsButtonCallback ?? (() => {})}
      disabled={
        integrationIsDeleted ||
        (validateMetadataRequestFields?.(
          definition.properties,
          channelIntegration.settings
        ) ??
          false)
      }
      textContent={buttonText}
    />
  );
};

const TenantMappingsField: React.FC<VantifyFieldProps> = ({
  channel,
  property,
  propertyKey,
  channelIntegration,
  onUpdateChannelIntegration,
  metadataLoading,
  fieldErrors,
}) => {
  const updateSettings = (value: VantifyTenantMappingData[]) => {
    onUpdateChannelIntegration({
      ...channelIntegration,
      settings: {
        ...channelIntegration.settings,
        [propertyKey]: value,
      },
    });
  };

  return (
    <div className={styles.propertyWrapper}>
      <VantifyTenantMapping
        channel={channel}
        label={property.friendlyName || property.name || propertyKey}
        onChange={updateSettings}
        metadataLoading={metadataLoading}
        settings={channelIntegration.settings}
      />
      <FieldErrorMessages errors={fieldErrors} />
    </div>
  );
};

const DomainField: React.FC<VantifyFieldProps> = ({
  propertyKey,
  property,
  channelIntegration,
  onUpdateChannelIntegration,
  fieldErrors,
}) => {
  const { t } = useTranslation();
  const isStringArray = (value: unknown): value is string[] => {
    return (
      Array.isArray(value) && value.every(item => typeof item === 'string')
    );
  };

  const updateSettings = (value: string[]) => {
    onUpdateChannelIntegration({
      ...channelIntegration,
      settings: {
        ...channelIntegration.settings,
        [propertyKey]: value,
      },
    });
  };

  return (
    <div className={styles.propertyWrapper}>
      <div className={styles.labelContainer}>
        <label className={styles.label}>{property.friendlyName}</label>
      </div>
      <DomainTagInput
        value={
          isStringArray(channelIntegration.settings?.[propertyKey])
            ? channelIntegration.settings[propertyKey]
            : []
        }
        onChange={updateSettings}
      />
      <div className={styles.hint}>
        {t('web.admin.channel.integrations.vantify.settings.domainHint')}
      </div>
      <FieldErrorMessages errors={fieldErrors} />
    </div>
  );
};

const useFieldLogic = (props: VantifyFieldProps) => {
  const {
    property,
    propertyKey,
    channelIntegration,
    onUpdateChannelIntegration,
    integrationIsDeleted,
    metadataPopulated,
  } = props;

  const isFieldDisabled = useMemo(
    () =>
      property.tags?.includes('metadataFieldResponse') && !metadataPopulated,
    [property.tags, metadataPopulated]
  );

  const currentValue = useMemo(
    () => channelIntegration.settings?.[propertyKey],
    [channelIntegration.settings, propertyKey]
  );

  const updateSettings = useCallback(
    (value: string | undefined | null, _fieldName: string) => {
      if (value !== undefined && value !== null) {
        onUpdateChannelIntegration({
          ...channelIntegration,
          settings: {
            ...channelIntegration.settings,
            [propertyKey]: value,
          },
        });
      }
    },
    [onUpdateChannelIntegration, channelIntegration, propertyKey]
  );

  return {
    isFieldDisabled,
    currentValue,
    updateSettings,
    disabled: integrationIsDeleted || isFieldDisabled,
  };
};

const InputField: React.FC<VantifyFieldProps> = props => {
  const { property, propertyKey, fieldErrors, channelIntegration, onUpdateChannelIntegration } = props;
  const { t } = useTranslation();
  const { currentValue, disabled, updateSettings } = useFieldLogic(props);
  const INPUT_TYPE_MAP: Record<string, InputType> = {
    Email: 'email',
    Password: 'password',
    Number: 'number',
    Em: 'number',
    Percentage: 'number',
    PhoneNumber: 'tel',
  };

  const getInputType = (propertyType: string): InputType => {
    return INPUT_TYPE_MAP[propertyType] ?? 'text';
  };

  const isSiteId = propertyKey === 'siteId';
  const siteName = isSiteId ? (channelIntegration.settings?.siteName ?? '') : '';
  const siteNameLabel = t('web.admin.channel.integrations.vantify.settings.site-name-label');

  const handleChange = useCallback(
    (value: string | undefined | null, fieldName: string) => {
      if (propertyKey === 'siteId' && String(value) !== String(currentValue)) {
        onUpdateChannelIntegration({
          ...channelIntegration,
          settings: {
            ...channelIntegration.settings,
            siteId: value ?? '',
            ...(channelIntegration.settings?.siteName && { siteName: '' }),
          },
        });

        return;
      }

      updateSettings(value, fieldName);
    },
    [propertyKey, currentValue, updateSettings, channelIntegration, onUpdateChannelIntegration]
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      // Don't trim password fields
      if (propertyKey === 'password') {
        return;
      }

      let trimmedValue = e.target.value?.trim() ?? '';

      if (propertyKey === 'apiUrl') {
        trimmedValue = trimmedValue.replace(/\/+$/, '');
      }

      if (trimmedValue !== String(currentValue || '')) {
        updateSettings(trimmedValue, propertyKey);
      }
    },
    [propertyKey, currentValue, updateSettings]
  );

  return (
    <div className={styles.propertyWrapper}>
      <Input
        fieldName={propertyKey}
        label={property.friendlyName}
        value={String(currentValue || '')}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={disabled}
        placeholder={property.placeholder}
        type={getInputType(property.type)}
        testId={property.friendlyName}
        maxExpand
        fixedLabel={!!property?.fixedLabel}
        error={fieldErrors}
      />
      {isSiteId && siteName && (
        <div className={styles.bottomBox}>
          <p className={styles.siteName}>
            {siteNameLabel} {siteName}
          </p>
        </div>
      )}
    </div>
  );
};

const CheckboxField: React.FC<VantifyFieldProps> = props => {
  const {
    property,
    propertyKey,
    channelIntegration,
    onUpdateChannelIntegration,
    fieldErrors,
  } = props;
  const { currentValue, disabled } = useFieldLogic(props);

  const handleCheckboxChange = useCallback(
    (_value: string, _name?: string) => {
      onUpdateChannelIntegration({
        ...channelIntegration,
        settings: {
          ...channelIntegration.settings,
          [propertyKey]: !currentValue,
        },
      });
    },
    [onUpdateChannelIntegration, channelIntegration, propertyKey, currentValue]
  );

  const isChecked = Boolean(currentValue);

  return (
    <div className={styles.propertyWrapper}>
      <Checkbox
        name={propertyKey}
        text={property.friendlyName}
        value={propertyKey}
        selected={isChecked}
        onChange={handleCheckboxChange}
        disabled={disabled}
        testId={property.friendlyName}
        style={{ padding: 0 }}
      />
      <FieldErrorMessages errors={fieldErrors} />
    </div>
  );
};

const VantifyField: React.FC<VantifyFieldProps> = props => {
  const { propertyKey } = props;

  switch (propertyKey) {
    case 'loadSettingButton':
      return <LoadSettingButtonField {...props} />;
    case 'tenantMappings':
      return <TenantMappingsField {...props} />;
    case 'approvedDomains':
      return <DomainField {...props} />;
    case 'siteId':
      return <InputField {...props} />;
    case 'autoApproveWorkOrders':
    case 'disableVantifyWebhookNotifications':
      return <CheckboxField {...props} />;
    default:
      return <InputField {...props} />;
  }
};

const validateMetadataRequestFields = (
  properties: PropertiesInterface,
  settings: VantifySettings
) => {
  return Object.entries(properties)
    .filter(([, prop]) => prop?.tags?.includes('metadataFieldRequest'))
    .some(([key]) => !settings?.[key]);
};

const getProperties = (
  properties: PropertiesInterface
): [string, PropertyType][] => {
  return Object.entries(properties);
};

const shouldHideField = (
  key: string,
  loadSettingsClicked: boolean,
  forCreate: boolean = false,
  webhookNotificationsEnabledFF: boolean = false,
  settings?: VantifySettings  
): boolean => {
  switch (key) {
    case 'tenantMappings':
    case 'autoApproveWorkOrders':
      return forCreate && !loadSettingsClicked;
    case 'approvedDomains':
      return !settings?.autoApproveWorkOrders;
    case 'disableVantifyWebhookNotifications':
      return !webhookNotificationsEnabledFF;
      
    default:
      return false;
  }
};

const validateAndConvertSiteId = (siteId: string | number | undefined | null): number => {
  if (siteId === undefined || siteId === null || siteId === '') {
    throw new Error('Site ID is required to load settings.');
  }

  const numericSiteId = typeof siteId === 'number' ? siteId : Number(siteId);

  if (Number.isNaN(numericSiteId)) {
    throw new Error('Site ID must be a valid number');
  }

  return numericSiteId;
};

const buildUpdatedChannelIntegration = (
  channelIntegration: ChannelIntegrationEditorProps<VantifySettings, Integration>['channelIntegration'],
  user: { name: string; id: number },
  siteDetails: { id: number; name: string }
) => {
  const currentSiteId = channelIntegration.settings?.siteId;
  const existingTenantMappings = Array.isArray(
    channelIntegration.settings?.tenantMappings
  )
    ? channelIntegration.settings.tenantMappings 
    : [];

  let currentSiteIdNumeric: number | undefined;

  if (currentSiteId !== undefined) {
    currentSiteIdNumeric =
      typeof currentSiteId === 'number'
        ? currentSiteId
        : Number(currentSiteId);
  }

  const siteIdChanged =
    currentSiteIdNumeric !== undefined &&
    !Number.isNaN(currentSiteIdNumeric) &&
    currentSiteIdNumeric !== siteDetails.id;

  return {
    ...channelIntegration,
    settings: {
      ...channelIntegration.settings,
      name: user.name,
      userId: user.id,
      siteId: siteDetails.id,
      siteName: siteDetails.name,
      tenantMappings: siteIdChanged ? [] : existingTenantMappings,
    },
  };
};

const createLoadSettingsButtonCallback =
  (params: LoadSettingsButtonCallbackParams) => async (): Promise<void> => {
  const {
    channelIntegration,
    fetchUsers,
    fetchSiteDetails,
    onUpdateChannelIntegration,
    setMetadataLoading,
    setMetadataError,
    setLocalValidationErrors,
    setLoadSettingsClicked,
  } = params;

  setLoadSettingsClicked(true);
  setMetadataLoading(true);
  setMetadataError('');
  setLocalValidationErrors(null);

  try {
    const { username, siteId } = channelIntegration.settings || {};
    
    if (!username || typeof username !== 'string' || username.trim() === '') {
      throw new Error('Username is required to load settings.');
    }

    const users = await fetchUsers({ username });

    if (!users || users.length === 0) {
      throw new Error('Failed to fetch user details. Please enter correct credentials.');
    }

    const numericSiteId = validateAndConvertSiteId(siteId);
    const siteDetails = await fetchSiteDetails(numericSiteId);

    if (!siteDetails) {
      throw new Error('Failed to fetch site details. Please enter a valid Site ID.');
    }
    
    onUpdateChannelIntegration(
      buildUpdatedChannelIntegration(channelIntegration, users[0], siteDetails)
    );

    setMetadataError('');
  } catch (error: unknown) {
    setMetadataError(
      error instanceof Error ? error.message : 'Failed to load settings'
    );
  } finally {
    setMetadataLoading(false);
  }
};

const useVantifyEditorState = (params: UseVantifyEditorStateParams) => {
  const {
    definition,
    fetchUsers,
    fetchSiteDetails,
    channelIntegration,
    onUpdateChannelIntegration,
    validationErrors,
  } = params;

  const [loadSettingsClicked, setLoadSettingsClicked] = useState(false);

  const [metadataLoading, setMetadataLoading] = useState(false);
  const [metadataError, setMetadataError] = useState('');
  const [localValidationErrors, setLocalValidationErrors] =
    useState<ValidationErrorType | null>(null);
  const cloneDefinition = useMemo(() => cloneDeep(definition), [definition]);
  const [localDefinition, setLocalDefinition] = useState(cloneDefinition);

  useEffect(() => setLocalDefinition(cloneDefinition), [cloneDefinition]);

  useEffect(() => {
    setLocalValidationErrors(validationErrors);
  }, [validationErrors]);

  const siteId = useMemo(() => {
    const { siteId: id, site } = channelIntegration.settings || {};

    const currentSiteId = id ?? (typeof site === 'object' ? site?.value : site);

    if (currentSiteId == null) return undefined;

    const numericId =
      typeof currentSiteId === 'number' ? currentSiteId : Number(currentSiteId);

    return Number.isNaN(numericId) ? undefined : numericId;
  }, [channelIntegration.settings]);

  const userId = useMemo(
    () => channelIntegration.settings?.userId,
    [channelIntegration.settings]
  );

  const loadSettingsButtonCallback = useMemo(
    () =>
      createLoadSettingsButtonCallback({
      channelIntegration,
      fetchUsers,
      fetchSiteDetails,
      onUpdateChannelIntegration,
      setMetadataLoading,
      setMetadataError,
      setLocalValidationErrors,
      setMetadataPopulated: () => {},
      setLocalDefinition,
      setLoadSettingsClicked,
    }),
    [
      channelIntegration,
      fetchUsers,
      fetchSiteDetails,
      onUpdateChannelIntegration,
      setLoadSettingsClicked,
    ]
  );

  const metadataPopulated = useMemo(() => {
    return Boolean(siteId && userId);
  }, [siteId, userId]);

  return {
    metadataLoading,
    metadataError,
    metadataPopulated,
    localDefinition,
    loadSettingsButtonCallback,
    localValidationErrors,
    loadSettingsClicked,
  };
};

const VantifyEditorContent: React.FC<{
  localDefinition: ChannelIntegrationEditorProps<
    VantifySettings,
    Integration
  >['definition'];
  channelIntegration: ChannelIntegrationEditorProps<
    VantifySettings,
    Integration
  >['channelIntegration'];
  onUpdateChannelIntegration: ChannelIntegrationEditorProps<
    VantifySettings,
    Integration
  >['onUpdateChannelIntegration'];
  channel: Channel;
  integrationIsDeleted?: boolean;
  metadataLoading: boolean;
  metadataError: string;
  metadataPopulated: boolean;
  loadSettingsButtonCallback: () => void;
  validationErrors: ValidationErrorType | null;
  forCreate?: boolean;
  t: (key: string) => string;
  loadSettingsClicked: boolean;
  webhookNotificationsEnabledFF: boolean;
}> = ({
  localDefinition,
  channelIntegration,
  onUpdateChannelIntegration,
  channel,
  integrationIsDeleted,
  metadataLoading,
  metadataError,
  metadataPopulated,
  loadSettingsButtonCallback,
  validationErrors,
  forCreate,
  t,
  loadSettingsClicked,
  webhookNotificationsEnabledFF,
}) => (
  <div>
    <H4 mb={2}>{t('web.admin.channel.integrations.vantify.settings.title')}</H4>
    {getProperties(localDefinition.properties).map(([key, property]) => {
      const fieldErrors =
        validationErrors && collectFieldErrors(validationErrors, key);

      const uniqueFieldErrors =
        fieldErrors && fieldErrors.length
          ? Array.from(new Set(fieldErrors))
          : null;

      if (key === 'subHeading') {
        return (
          <H5 key={key} mb={5}>
            {property?.friendlyName}
          </H5>
        );
      }

      if (
        shouldHideField(
          key,
          loadSettingsClicked,
          forCreate,
          webhookNotificationsEnabledFF,
          channelIntegration.settings
        )
      ) {
        return null;
      }

      return (
        <div key={key} className={styles.property}>
          <VantifyField
            property={property}
            propertyKey={key}
            channelIntegration={channelIntegration}
            onUpdateChannelIntegration={onUpdateChannelIntegration}
            channel={channel}
            definition={localDefinition}
            integrationIsDeleted={integrationIsDeleted}
            metadataLoading={metadataLoading}
            metadataError={metadataError}
            metadataPopulated={metadataPopulated}
            loadSettingsButtonCallback={loadSettingsButtonCallback}
            validateMetadataRequestFields={validateMetadataRequestFields}
            fieldErrors={uniqueFieldErrors}
            forCreate={forCreate}
          />
        </div>
      );
    })}
  </div>
);

export function VantifyEditor({
  channelIntegration,
  onUpdateChannelIntegration,
  channel,
  integrationIsDeleted,
  definition,
  validationErrors = null,
  forCreate,
  webhookNotificationsEnabledFF = false,
}: VantifyEditorProps) {
  const { t } = useTranslation();
  const { fetchUsers } = useVantifyUser();
  const { fetchSiteDetails } = useVantifySiteDetails();
  const autoApproveEnabled = channelIntegration.settings?.autoApproveWorkOrders === true;
  const approvedDomains = channelIntegration.settings?.approvedDomains;

  // Ensure approvedDomains exists when auto-approve is enabled so validation runs.
  useEffect(() => {
    if (!autoApproveEnabled || Array.isArray(approvedDomains)) {
      return;
    }

    onUpdateChannelIntegration({
      ...channelIntegration,
      settings: {
        ...channelIntegration.settings,
        approvedDomains: [],
      },
    });
  }, [autoApproveEnabled]);

  const {
    metadataLoading,
    metadataError,
    metadataPopulated,
    localDefinition,
    loadSettingsButtonCallback,
    localValidationErrors,
    loadSettingsClicked,
  } = useVantifyEditorState({
    definition,
    fetchUsers,
    fetchSiteDetails,
    channelIntegration,
    onUpdateChannelIntegration,
    validationErrors,
  });

  return (
    <VantifyEditorContent
      localDefinition={localDefinition}
      channelIntegration={channelIntegration}
      onUpdateChannelIntegration={onUpdateChannelIntegration}
      channel={channel}
      integrationIsDeleted={integrationIsDeleted}
      metadataLoading={metadataLoading}
      metadataError={metadataError}
      metadataPopulated={metadataPopulated}
      loadSettingsButtonCallback={loadSettingsButtonCallback}
      validationErrors={localValidationErrors}
      forCreate={forCreate}
      t={t}
      loadSettingsClicked={loadSettingsClicked}
      webhookNotificationsEnabledFF={webhookNotificationsEnabledFF}
    />
  );
}
